import React, { PureComponent } from "react";
import * as Icon from "react-feather";

import { operatorDef } from "./operatorDefinitions";
import FilterCombobox from "./FilterCombobox";

/**
 * Renders the right value-input UI based on (field.type, op).
 * Pure: doesn't manage value state — props.value + props.onChange.
 *
 * `field` shape:
 *   { key, label, type, options?: [{ key|id, label, color? }] | (ctx) => [...] }
 */
class FilterValueInput extends PureComponent {
	_resolveOptions() {
		const { field, ctx } = this.props;
		if (!field) return [];
		if (typeof field.options === "function") {
			try {
				return field.options(ctx || {}) || [];
			} catch (e) {
				return [];
			}
		}
		return field.options || [];
	}

	render() {
		const { field, op, value, onChange } = this.props;
		if (!field || !op) return null;

		const def = operatorDef(field.type, op);
		if (!def) return null;

		switch (def.input) {
			case "none":
				return <span className="filter-value-input__none">—</span>;

			case "text":
				return (
					<input
						className="filter-value-input"
						type="text"
						value={value == null ? "" : value}
						placeholder="Enter value"
						onChange={e => onChange(e.target.value)}
					/>
				);

			case "number":
				return (
					<input
						className="filter-value-input"
						type="number"
						value={value == null ? "" : value}
						placeholder="0"
						onChange={e => onChange(e.target.value === "" ? "" : Number(e.target.value))}
					/>
				);

			case "int":
				return (
					<input
						className="filter-value-input filter-value-input--int"
						type="number"
						min="0"
						step="1"
						value={value == null ? "" : value}
						placeholder="0"
						onChange={e => {
							const n = e.target.value === "" ? "" : parseInt(e.target.value, 10);
							onChange(isNaN(n) ? "" : n);
						}}
					/>
				);

			case "date":
				return (
					<input
						className="filter-value-input"
						type="date"
						value={value == null ? "" : value}
						onChange={e => onChange(e.target.value || null)}
					/>
				);

			case "date_range": {
				const arr = Array.isArray(value) ? value : ["", ""];
				return (
					<div className="filter-value-input__range">
						<input
							className="filter-value-input"
							type="date"
							value={arr[0] || ""}
							onChange={e => onChange([e.target.value || null, arr[1]])}
						/>
						<span className="filter-value-input__range-sep">to</span>
						<input
							className="filter-value-input"
							type="date"
							value={arr[1] || ""}
							onChange={e => onChange([arr[0], e.target.value || null])}
						/>
					</div>
				);
			}

			case "number_range": {
				const arr = Array.isArray(value) ? value : ["", ""];
				return (
					<div className="filter-value-input__range">
						<input
							className="filter-value-input"
							type="number"
							value={arr[0] == null ? "" : arr[0]}
							onChange={e => onChange([e.target.value === "" ? "" : Number(e.target.value), arr[1]])}
						/>
						<span className="filter-value-input__range-sep">to</span>
						<input
							className="filter-value-input"
							type="number"
							value={arr[1] == null ? "" : arr[1]}
							onChange={e => onChange([arr[0], e.target.value === "" ? "" : Number(e.target.value)])}
						/>
					</div>
				);
			}

			case "boolean":
				return (
					<div className="filter-value-input__bool">
						<label>
							<input
								type="radio"
								checked={value === true || value === "true" || value === 1 || value === "1"}
								onChange={() => onChange(true)}
							/>
							<span>Yes</span>
						</label>
						<label>
							<input
								type="radio"
								checked={value === false || value === "false" || value === 0 || value === "0"}
								onChange={() => onChange(false)}
							/>
							<span>No</span>
						</label>
					</div>
				);

			case "picklist": {
				// Typeable combobox — searchable for long datasource lists. Labels
				// that share a name across groups get the group appended so they're
				// distinguishable in the flat list.
				const options = this._resolveOptions().map(o => ({
					value: o.id != null ? o.id : o.key,
					label: (o.label || o.name) + (o.group ? " · " + o.group : ""),
					group: o.group,
				}));
				return (
					<FilterCombobox
						options={options}
						value={value}
						placeholder="Choose…"
						onChange={v => {
							if (v == null || v === "") onChange(null);
							else onChange(isNaN(Number(v)) ? v : Number(v));
						}}
					/>
				);
			}

			case "picklist_multi": {
				const options = this._resolveOptions();
				const selected = Array.isArray(value) ? value.map(String) : [];
				const toggle = id => {
					const s = String(id);
					const next = selected.includes(s) ? selected.filter(x => x !== s) : [...selected, s];
					onChange(next.map(v => (isNaN(Number(v)) ? v : Number(v))));
				};
				return (
					<div className="filter-value-input__multi">
						{options.length === 0 && <span className="filter-value-input__none">No options</span>}
						{options.map(o => {
							const id = o.id != null ? o.id : o.key;
							const checked = selected.includes(String(id));
							// Append group (e.g. flow name) to chip label so duplicate
							// labels across groups are visually distinguishable.
							const label = (o.label || o.name) + (o.group ? " · " + o.group : "");
							return (
								<button
									key={id}
									type="button"
									className={
										"filter-value-input__chip " + (checked ? "filter-value-input__chip--on" : "")
									}
									onClick={() => toggle(id)}
								>
									{checked && <Icon.Check size={11} />}
									<span>{label}</span>
								</button>
							);
						})}
					</div>
				);
			}

			default:
				return null;
		}
	}
}

export default FilterValueInput;
